import { notify } from "@cayuse/shared-react.components.banner-notification";
import update from "immutability-helper";
import { cloneDeep, isEqual, omit } from "lodash";
import { v4 as uuid } from "uuid";

import { IS_DEV, RESEARCH_TEAM_ROLE_VALIDATION_ERROR } from "globals/constants";
import { api } from "services/api";
import { researchRoles as constants } from "store/constants";
import { dispatch } from "store/store";
import { notifyError } from "utils";

import { actionLoaderFactory } from "../factories/fetchable";
import { researchRolesSelectors } from "../selectors";

const factory = actionLoaderFactory(constants, constants.RESEARCH_ROLES);

const onError = (error, storeError) => {
	const message = IS_DEV ? error.message : "Something went wrong";

	notifyError(message, error);
	storeError(message);
};

const normalizeRoles = roles => {
	return roles
		.map(it => ({
			...it,
			isPrincipalInvestigatorRole: it.researchRoleId === "principal-investigator",
			error: null,
		}))
		.sort((a, b) => a.position - b.position);
};

export const setData = ({ items, totalItems }) => {
	factory.setData({
		items: normalizeRoles(items),
		totalItems: totalItems ?? items.length,
	});
};

export const findAll = () => {
	return factory.createLoaderAction(
		() => api.researchRoles.findAll({ size: 100, activeOnly: false }),
		[
			response => {
				setData({ items: response.content, totalItems: response.totalElements });
			},
			error => onError(error, factory.setError),
		],
	);
};

const setIsChanged = () => {
	const { source, changed } = researchRolesSelectors.content();

	const sourceMap = new Map(source.map(it => [ it.researchRoleId, it ]));

	let _hasChanges = false;
	for (const changedElement of changed) {
		const originalElement = sourceMap.get(changedElement.researchRoleId);

		const cleanOriginalElement = omit(originalElement, [ "error" ]);
		const cleanChangedElement = omit(changedElement, [ "error" ]);

		if (!isEqual(cleanOriginalElement, cleanChangedElement)) {
			_hasChanges = true;
			break;
		}
	}

	dispatch({
		type: constants.RESEARCH_ROLES_SET_WAS_CHANGED,
		payload: _hasChanges,
	});
};


export function editRoleById (id, changes) {
	const currentRoles = researchRolesSelectors.content().changed;

	const changed = cloneDeep(currentRoles)
		.map((it, index) => {
			if (it.researchRoleId === id || it.tempResearchRoleId === id) {
				return {
					...it,
					...changes,
				};
			}

			return it;
		});

	const updatedFieldValues = changed.map(it => it.name);
	const validatedChanged = changed.map((it, index) => {
		const error = validateName(index, it.name, updatedFieldValues);

		return {
			...it,
			error,
		};
	});

	dispatch({
		type: constants.RESEARCH_ROLES_SET_CHANGED_DATA,
		payload: {
			changed: validatedChanged,
		},
	});

	setIsChanged();
}

export const findRoleInArray = id => {
	const array = researchRolesSelectors.content().changed;

	const item = array.filter(c => getId(c) === id)[0];

	return {
		item,
		index: array.indexOf(item),
	};
};

export const moveRole = (id, atIndex) => {
	const changed = cloneDeep(researchRolesSelectors.content().changed);
	if (atIndex <= 0 || atIndex >= changed.length) {
		return;
	}

	const { item, index } = findRoleInArray(id);

	const moved = update(changed, {
		$splice: [
			[index, 1],
			[atIndex, 0, cloneDeep(item)],
		],
	});

	moved.forEach((it, idx) => {
		it.position = idx;
	});

	dispatch({
		type: constants.RESEARCH_ROLES_SET_CHANGED_DATA,
		payload: {
			changed: moved,
		},
	});

	setIsChanged();
};

export const addEmptyElement = () => {
	const changed = cloneDeep(researchRolesSelectors.content().changed);

	const lastItemPosition = changed.at(-1).position;

	changed.push({
		tempResearchRoleId: uuid(),
		name: "",
		active: false,
		position: lastItemPosition + 1,
		error: RESEARCH_TEAM_ROLE_VALIDATION_ERROR.EMPTY_FIELD,
	});

	dispatch({
		type: constants.RESEARCH_ROLES_SET_CHANGED_DATA,
		payload: {
			changed,
		},
	});

	setIsChanged();
};

export const resetChanges = () => {
	dispatch({
		type: constants.RESEARCH_ROLES_SET_DATA,
		payload: {
			items: cloneDeep(researchRolesSelectors.content().source),
		},
	});

	setIsChanged();
};

export const saveChanged = async () => {
	try {
		const changed = normalizeChanged(researchRolesSelectors.content().changed);

		const response = await api.researchRoles.batchUpdate(changed);

		setData({ items: response });
		notify(
			"Your changes have been saved",
			{ type: "success", autoClose: true },
		);

		return true;
	} catch {
		return false;
	} finally {
		setIsChanged();
	}
};

export const getId = item => {
	return item.researchRoleId || item.tempResearchRoleId;
};

const validateName = (index, value, fieldValues) => {
	if (!value || value.trim().length < 1) {
		return RESEARCH_TEAM_ROLE_VALIDATION_ERROR.EMPTY_FIELD;
	} else if (value.trim().length > 100) {
		return RESEARCH_TEAM_ROLE_VALIDATION_ERROR.MAXIMUM_CHARACTER_LIMIT_REACHED;
	} else if (fieldValues.some((name, i) => i !== index && name.trim().toLowerCase() === value.trim().toLowerCase())) {
		return RESEARCH_TEAM_ROLE_VALIDATION_ERROR.NOT_UNIQUE;
	}
};

const normalizeChanged = changed => {
	return changed.map(it => {
		// Remove 'front-end' keys
		it = omit(it, [ "isPrincipalInvestigatorRole", "tempResearchRoleId", "error" ]);

		return { ...it, name: it.name.trim() };
	});
};
